import { useQuery } from '@tanstack/react-query';
import { getAllBuyers } from '@/services/buyerAPI';

export interface Customer {
  id: number;
  name: string; // Maps from companyName in Buyer interface
  companyName?: string; // Original company name
  contactPerson?: string; // Maps from contactPersonName
  phone?: string; // Maps from primaryPhone
  email?: string;
  creditLimit?: number;
  gstNumber?: string;
  panNumber?: string;
  contractStatus?: string;
  city?: string;
  registeredAddress?: string;
}

/**
 * Custom hook to fetch active customers from the backend
 * Used for customer selection in order creation
 *
 * - Fetches only ACTIVE customers (contractStatus = 'ACTIVE')
 * - Sorts alphabetically by company name
 * - 1-minute cache for order creation pages to show newly added customers
 * - Throws error if backend is unavailable (no mock fallback)
 *
 * @returns Query result with active customers list
 */
export function useCustomers() {
  return useQuery({
    queryKey: ['customers', 'active'],
    queryFn: async (): Promise<Customer[]> => {
      try {
        // Fetch ONLY ACTIVE customers from the backend using buyerAPI
        // GET /api/v1/customers?status=ACTIVE (returns paginated response)
        const response = await getAllBuyers({
          page: 0,
          size: 1000, // Get all active customers
          sortBy: 'companyName', // Sort alphabetically
          sortDir: 'asc',
        });

        // Handle response - buyerAPI returns unwrapped response
        // After microservicesClient unwrapping: { content: [...], totalElements: N, ... }
        if (!response) {
          console.error('Empty response from customers endpoint');
          throw new Error('No active customers found. Please try again.');
        }

        console.log('useCustomers raw response:', response);

        // Extract customers from response - check all possible formats
        let customers: any[] = [];

        // Primary: direct content array (after microservicesClient unwrapping)
        if (response.content && Array.isArray(response.content)) {
          customers = response.content;
          console.log('Found customers in response.content:', customers.length);
        }
        // Fallback: nested data.content
        else if (response.data?.content && Array.isArray(response.data.content)) {
          customers = response.data.content;
          console.log('Found customers in response.data.content:', customers.length);
        }
        // Fallback: direct data array
        else if (response.data && Array.isArray(response.data)) {
          customers = response.data;
          console.log('Found customers in response.data (array):', customers.length);
        }
        // Fallback: response itself is array
        else if (Array.isArray(response)) {
          customers = response;
          console.log('Found customers in response (array):', customers.length);
        }

        if (!Array.isArray(customers)) {
          console.error('Invalid customers response format:', response);
          throw new Error('Invalid customer data format');
        }

        // Filter valid customers and map from buyerAPI Buyer interface to Customer interface
        // Include ALL customers (ACTIVE, APPROVED, PENDING) to show newly created customers from inquiry flow
        // This ensures that newly created customers appear in order creation dropdown immediately
        const activeCustomers: Customer[] = customers
          .filter(buyer =>
            // Include all customers except those explicitly marked as INACTIVE or REJECTED
            buyer.contractStatus !== 'INACTIVE' &&
            buyer.onboardingStatus !== 'REJECTED'
          )
          .map(buyer => {
            // Extract city from registeredAddress if not directly available
            let city = '';
            if (buyer.registeredAddress) {
              // Address format: "street, city, state - pincode, country" or "street, city, state pincode"
              const parts = buyer.registeredAddress.split(',');
              if (parts.length >= 2) {
                city = parts[1]?.trim() || '';
              }
            }

            return {
              id: buyer.id,
              name: buyer.name || buyer.companyName, // Handle both field names
              companyName: buyer.companyName || buyer.name,
              contactPerson: buyer.contactPerson || buyer.contactPersonName,
              phone: buyer.phone || buyer.primaryPhone,
              email: buyer.email,
              creditLimit: buyer.creditLimit,
              gstNumber: buyer.gstNumber,
              panNumber: buyer.panNumber,
              contractStatus: buyer.contractStatus,
              city: city,
              registeredAddress: buyer.registeredAddress,
            };
          });

        console.log('Fetched active customers:', activeCustomers.length);
        return activeCustomers;
      } catch (error) {
        console.error('Error fetching customers:', error);
        if (error instanceof Error) {
          console.error('Error details:', error.message);
        }
        // Re-throw error to be handled by component
        throw error;
      }
    },
    staleTime: 30 * 1000, // 30 seconds - shorter cache to catch newly created customers quickly
    gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in React Query v5)
    refetchOnMount: 'always', // Always refetch on mount to show newly created customers
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
